import { PDFInfo } from "@/wbcontroller/PDFController";
import { ShapeType } from "@/wbcontroller/types/ShapeType";
import ToolType from "@/wbcontroller/types/ToolType";
import { UserInfo } from "@/wbcontroller/types/UserInfo";
import { WBSelection } from "@/wbcontroller/types/WBSelection";
import { WBState, WhiteboardData } from "./stateInterfaceType";
import { WhiteboardController } from "@/wbcontroller/WhiteboardController";
import ControllerCache from "./ControllerCache";
import makeTypeSafeCommitter from "./makeTypeSafeCommitter";

const mutations = {
  storeWhiteboardController(state: WBState, controller: WhiteboardController) {
    ControllerCache[controller.wbid] = controller;
  },
  removeWhiteboardController(state: WBState, wbid: string) {
    delete ControllerCache[wbid];
  },

  applyNavigation(state: WBState, wbid: string) {
    if (wbid === state.whiteboard.wbid) return;
    if (state.folder && state.folderNavigation.index.get(wbid)) {
      for (const child of state.folder.whiteboards) {
        if (child.wbid == wbid) {
          state.whiteboard = child;
          return;
        }
      }
    }
    // We don't have the whiteboard object, so do a full navigation
    window.location.href = `/whiteboard/${wbid}`;
  },

  setIsLocked(state: WBState, isLocked: boolean) {
    state.isLocked = isLocked;
  },

  /** Also open the PDFImport modal! */
  requestInsertPDF(state: WBState, info: PDFInfo) {
    state.pendingPDF = info;
  },

  finishInsertingPDF(state: WBState) {
    state.pendingPDF = null;
  },

  // tool state
  setStrokeColor(state: WBState, color: string) {
    state.hud.changedKeys = ["strokeColor"];
    state.hud.strokeColor = color;
    localStorage.strokeColor = color; // debugging only
    if (state.selectedShapes.length === 0) {
      state.hud.strokeColorNoSelection = color;
    }
  },
  setFillColor(state: WBState, color: string) {
    state.hud.changedKeys = ["fillColor"];
    state.hud.fillColor = color;
    localStorage.fillColor = color; // debugging only
    if (state.selectedShapes.length === 0) {
      state.hud.fillColorNoSelection = color;
    }
  },
  setFillColorForSticky(state: WBState, color: string) {
    state.hud.changedKeys = ["fillColorForSticky"];
    state.hud.fillColorForSticky = color;
    if (state.selectedShapes.length === 0) {
      state.hud.fillColorForStickyNoSelection = color;
    }
  },
  setStrokeWidth(state: WBState, w: number) {
    state.hud.changedKeys = ["strokeWidth"];
    state.hud.strokeWidth = w;
    if (state.selectedShapes.length === 0) {
      state.hud.strokeWidthNoSelection = w;
    }
  },
  setStrokeWidthForEraser(state: WBState, w: number) {
    state.hud.changedKeys = [];
    state.hud.strokeWidthForEraser = w;
  },
  setOpacity(state: WBState, o: number) {
    state.hud.changedKeys = ["opacity"];
    state.hud.opacity = o;
  },

  setActiveUsers(state: WBState, map: Record<string, UserInfo>) {
    state.activeUsers = Object.assign({}, map);
  },
  setTool(state: WBState, toolType: ToolType) {
    state.hud.changedKeys = [];
    state.hud.tool = toolType;
    localStorage.lastToolSelected = toolType;
  },
  setSelections(state: WBState, selections: WBSelection[]) {
    state.selectedShapes = selections;

    state.hud.changedKeys = [];
    if (selections.length === 0) {
      state.hud.strokeColor = state.hud.strokeColorNoSelection;
      state.hud.strokeWidth = state.hud.strokeWidthNoSelection;
      state.hud.fillColor = state.hud.fillColorNoSelection;
      state.hud.fillColorForSticky = state.hud.fillColorNoSelection;
    } else if (selections.length === 1) {
      if (selections[0].strokeColor)
        state.hud.strokeColor = selections[0].strokeColor;
      if (selections[0].strokeWidth)
        state.hud.strokeWidth = selections[0].strokeWidth;
      if (selections[0].fillColor)
        state.hud.fillColor = selections[0].fillColor;
      if (selections[0].fillColor && selections[0].shapeType === ShapeType.Node)
        state.hud.fillColorForSticky = selections[0].fillColor;
    } else {
      // do nothing
    }
  },
  openStrokeColorPicker(state: WBState) {
    state.hudUI.isColorPickerOpen = true;
  },
  closeColorPicker(state: WBState) {
    state.hudUI.isColorPickerOpen = false;
  },

  setWBMetadata(state: WBState, metadata: Partial<WhiteboardData>) {
    state.whiteboard = { ...state.whiteboard, ...metadata };
  },
};
export default mutations;

export const GlobalMutations = makeTypeSafeCommitter<WBState, typeof mutations>(
  mutations
);
